import type {FsSimpleData} from '@/services/auditService/auditAdjustmentService';
import type {ASectionPageCode} from '@/pages/components/audit-program/a-section/constants';
import {buildVisibleAiIndex, deriveActiveIndexFromFs} from '@/pages/components/audit-program/ai-section/deriveActiveIndex';
import type {AiIndexEntry} from '@/pages/components/audit-program/ai-section/constants';
import {getAppendixDef} from '@/pages/components/audit-program/appendix';

export type ProgramViewKey = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H' | 'I' | 'J' | 'K' | 'L' | 'M' | 'N' | 'O' | 'P' | 'Q' | 'R' | 'S' | 'T' | 'V' | 'W' | 'X' | 'Y' | 'Z' | 'PAF' | 'APPENDIX';

export interface ProgramDirectoryItem {
    key: string;
    code: string;
    title: string;
    kind: 'letter' | 'appendix';
    ready: boolean;
    view?: ProgramViewKey;
    aSectionPage?: ASectionPageCode;
    /** Ai appendix code ('1'…'6') when kind === 'appendix'. */
    appendixCode?: string;
}

const LETTER_ROUTES: Record<string, Pick<ProgramDirectoryItem, 'ready' | 'view' | 'aSectionPage'>> = {
    A: {ready: true, view: 'A', aSectionPage: 'Ai'},
    B: {ready: true, view: 'B'},
    C: {ready: true, view: 'C'},
    D: {ready: true, view: 'D'},
    E: {ready: true, view: 'E'},
    F: {ready: true, view: 'F'},
    G: {ready: true, view: 'G'},
    H: {ready: true, view: 'H'},
    I: {ready: true, view: 'I'},
    J: {ready: true, view: 'J'},
    K: {ready: true, view: 'K'},
    L: {ready: true, view: 'L'},
    M: {ready: true, view: 'M'},
    N: {ready: true, view: 'N'},
    O: {ready: true, view: 'O'},
    P: {ready: true, view: 'P'},
    Q: {ready: true, view: 'Q'},
    R: {ready: true, view: 'R'},
    S: {ready: true, view: 'S'},
    T: {ready: true, view: 'T'},
    V: {ready: true, view: 'V'},
    W: {ready: true, view: 'W'},
    X: {ready: true, view: 'X'},
    Y: {ready: true, view: 'Y'},
    Z: {ready: true, view: 'Z'},
    PAF: {ready: true, view: 'PAF'},
};

function toDirectoryItem(entry: AiIndexEntry): ProgramDirectoryItem {
    const key = entry.kind === 'appendix' ? `appendix-${entry.code}` : entry.code;
    if (entry.kind === 'appendix') {
        const def = getAppendixDef(entry.code);
        return {
            key,
            code: entry.code,
            title: entry.title,
            kind: 'appendix',
            ready: Boolean(def),
            view: def ? 'APPENDIX' : undefined,
            appendixCode: entry.code,
        };
    }
    const route = LETTER_ROUTES[entry.code];
    return {
        key,
        code: entry.code,
        title: entry.title,
        kind: 'letter',
        ready: route?.ready ?? false,
        view: route?.view,
        aSectionPage: route?.aSectionPage,
    };
}

export function convertVoToFsSimpleData(vo: Record<string, unknown>): FsSimpleData {
    const children = Array.isArray(vo.children)
        ? vo.children.map((c) => convertVoToFsSimpleData(c as Record<string, unknown>))
        : [];
    return {
        fsId: Number(vo.fsId ?? 0),
        level: Number(vo.level ?? 0),
        name: String(vo.name ?? ''),
        code: String(vo.code ?? ''),
        entry: (String(vo.entry ?? '') || 'Dr') as 'Dr' | 'Cr',
        scheduleName: String(vo.scheduleName ?? ''),
        scheduleCode: String(vo.scheduleCode ?? ''),
        openingAmount: Number(vo.openingAmount ?? vo.client ?? 0),
        adjustment: [],
        finalAmount: Number(vo.audited ?? vo.finalAmount ?? 0),
        lastYearAudited: Number(vo.lastYearAudited ?? 0),
        adjustByPaper: Boolean(vo.adjustByPaper),
        wpStatus: vo.wpStatus == null ? null : Number(vo.wpStatus),
        children,
    };
}

export function buildProgramDirectory(bsLine: FsSimpleData[], plLine: FsSimpleData[]): ProgramDirectoryItem[] {
    const active = deriveActiveIndexFromFs(bsLine, plLine);
    const visible = buildVisibleAiIndex(active);
    return [...visible.letters, ...visible.appendices].map(toDirectoryItem);
}

export function defaultDirectoryKey(items: ProgramDirectoryItem[]): string {
    const preferred = items.find((i) => i.code === 'A') ?? items.find((i) => i.ready) ?? items[0];
    return preferred?.key ?? 'A';
}
